import {AutoComplete} from "material-ui";
import {MuiThemeProvider} from "material-ui/styles";
import * as React from "react";
import {useEffect, useState} from "react";
import {FormattedMessage} from "react-intl";
import {connect} from "react-redux";
import {AnyAction, bindActionCreators, Dispatch} from "redux";
import {navigateTo} from "../../../navigateTo";
import {getElementsByTagName} from "../../api/BrowserWrapper";
import {ResearchSearchItem} from "../../model/Search.model";
import {getElementById, getScrollOffset} from "../../utils/browserUtil";
import ProductActions, {default as productActions} from "../product/productActions";
import {getTranslation} from "../../utils/translationUtil";
import {AonColors} from "../../../../css/Colors";

export const HEADER_HEIGHT = 64;
const NO_RESULTS_MESSAGE = "No strategy or manager results found.";

export interface ISubNavPropsFromActions {
    actions: {
        selectTicker: typeof ProductActions.selectTicker;
    };
}

export interface ISubNavMenuItem {
    id: string;
    name: string;
}

export interface ISubNavPropsFromParent {
    menuItems: ISubNavMenuItem[];
    searchEnabled: boolean;
    onItemSelected: (id: string) => void;
    selectedMenuItem: string;
    searchItems?: ResearchSearchItem[];
}

export type ISubNavProps =
    ISubNavPropsFromParent
    & ISubNavPropsFromActions;

export const SubNavComponent: React.FunctionComponent<ISubNavProps> = (props) => {
    const [searchVisible, setSearchVisible] = useState<boolean>(false);
    const [topOfSubNavY, setTopOfSubNavY] = useState<number>(HEADER_HEIGHT);
    const [searchItems, setSearchItems] = useState<ResearchSearchItem[]>([]);
    const [searchText, setSearchText] = useState<string>("");

    let highlightedItem: ResearchSearchItem; // not state since component should not rerender when changed

    const NO_RESULTS_MESSAGE_TRANSLATED = getTranslation("subnav.search-no-results", NO_RESULTS_MESSAGE);

    function searchByWordFilter(textToSearch: string, displayText: string): boolean {
        const searchWords = textToSearch.split(" ").map((x) => x.trim());
        const displayWords = displayText.split(" ").map((x) => x.trim());

        const someDisplayWordMatches = (searchWord: string) =>
            displayWords.some((displayWord: string) =>
                AutoComplete.caseInsensitiveFilter(searchWord, displayWord));
        return searchWords.every((searchWord: string) => someDisplayWordMatches(searchWord));
    }

    function handleScroll() {
        setTopOfSubNavY(Math.max(0, HEADER_HEIGHT - getScrollOffset()));
    }

    function subNavScrolling() {
        return isScrolling() ? " sub-nav--scrolling" : "";
    }

    function renderSearch() {
        return searchVisible ? renderSearchBar() : renderSearchIcon();
    }

    function renderNavItems() {
        return props.menuItems
            .map((item) => {
                const selected = props.selectedMenuItem === item.id;
                return <a key={item.id}
                            id={`sub-nav__item-${item.id}`}
                            className={`sub-nav__item${selected ? " sub-nav__item--selected" : ""}`}
                            onClick={() => {
                                handleNavItemClick(item);
                            }}>
                    <FormattedMessage id={`subnav.${item.id}`}
                                      defaultMessage={item.name} />
                </a>;
            });
    }

    function handleNavItemClick(item: ISubNavMenuItem) {
        setSearchText("");
        setSearchVisible(false);
        props.onItemSelected(item.id);
    }

    function renderSearchBar() {
        return <div className="search__inner-container">
            <div id="menu-search-icon" className="search__icon far fa-search"/>
            {renderSearchNav()}
        </div>;
    }

    function renderSearchIcon() {
        return <div className="search__inner-container">
            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
            <div className="search__clickable-container clickable" onClick={showSearch}>
                <div id="menu-search-icon" className="search__icon far fa-search"/>
                <div className="search__header">
                   <FormattedMessage id="subnav.search" defaultMessage="Search" />
                </div>
            </div>
        </div>;
    }

    function handleSearchItemSelected(item: ResearchSearchItem) {
        if (item.itemType !== "NOT_FOUND") {
            if (item.tickerId) {
                props.actions.selectTicker({tickerId: item.tickerId, productId: item.backstopId});
            }

            setSearchVisible(false);
            setSearchText("");

            navigateTo(item.pageUrl);
        } else {
            setSearchText("");
        }
    }

    function searchFilter(textToSearch: string, displayText: string): boolean {
        if (textToSearch.length < 2) {
            return false;
        }

        if (displayText === NO_RESULTS_MESSAGE_TRANSLATED) {
            const matchCount = props.searchItems!
                .map((product: any) => searchByWordFilter(textToSearch, product.displayName))
                .filter((visibleProduct: boolean) => visibleProduct).length;

            return matchCount === 0;
        }
        return searchByWordFilter(textToSearch, displayText);
    }

    function isScrolling() {
        return topOfSubNavY === 0;
    }

    async function showSearch() {
        await setSearchVisible(true);
        const parent = getElementById("autocomplete-container");
        (getElementsByTagName(parent, "input")[0] as HTMLElement).focus();
    }

    function clearSearchText() {
        setSearchText("");
        const parent = getElementById("autocomplete-container");
        (getElementsByTagName(parent, "input")[0] as HTMLElement).focus();
    }

    function setItemsToSearch() {
        if (props.searchItems) {
            const items = props.searchItems.slice(0).sort((a, b) => {
                return a.displayName.trim().toLowerCase().localeCompare(b.displayName.trim().toLowerCase());
            });
            items.push(new ResearchSearchItem(-1, NO_RESULTS_MESSAGE_TRANSLATED, "NOT_FOUND", "", undefined));

            setSearchItems(items);
        }
    }

    function renderSearchNav() {
        return <div className="search__input-box">
            <div className="search__close-icon-box">
                <div className="search__close-icon fal fa-times clickable" onClick={clearSearchText}/>
            </div>
            <div id="autocomplete-container">
                <MuiThemeProvider>
                    <AutoComplete
                        className="search__search-field clickable"
                        onUpdateInput={(text: string) => {
                            setSearchText(text);
                        }}
                        searchText={searchText}
                        filter={searchFilter}
                        underlineStyle={{bottom: 10, borderBottomColor: AonColors.AonTealDark}}
                        hintStyle={{bottom: 10}}
                        menuProps={{
                            onKeyPress: (event: any) => {
                                if (event.key === "Enter" && highlightedItem) {
                                    handleSearchItemSelected(highlightedItem);
                                }
                            },
                            onMenuItemFocusChange: (event: object, newFocusIndex: number) => {
                                const filtered = searchItems.filter((item) =>
                                    searchFilter(searchText, item.displayName));
                                highlightedItem = filtered[newFocusIndex];
                            },
                        }}
                        dataSource={searchItems}
                        popoverProps={{className: "ie-white-scrollbar search-popover"}}
                        dataSourceConfig={{text: "displayName", value: "backstopId"}}
                        onNewRequest={handleSearchItemSelected}
                        hintText={getTranslation("subnav.search-managers-strategies", "Search managers or strategies")}
                        fullWidth={true}
                        listStyle={{maxHeight: 600, overflow: "auto"}}
                    />
                </MuiThemeProvider>
            </div>
        </div>;
    }

    useEffect(() => {
        setItemsToSearch();
        window.addEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        return () => {
            window.removeEventListener("scroll", handleScroll);
        };
    });

    useEffect(() => {
        setItemsToSearch();
    }, [props]);

    return <div className={`sub-nav ${subNavScrolling()}`}>
        <div className="sub-nav__content">
            <div className="sub-nav__spacer"/>
            <div className="sub-nav__items">
                {renderNavItems()}
            </div>
            <div className="sub-nav__search-container">
                {props.searchEnabled ? renderSearch() : null}
            </div>
        </div>
    </div>;
};

export const mapDispatchToProps = (dispatch: Dispatch<AnyAction>): ISubNavPropsFromActions => {
    return {
        actions: bindActionCreators({
            selectTicker: productActions.selectTicker,
        }, dispatch),
    };
};

const SubNav = connect<any, ISubNavPropsFromActions, any>(undefined, mapDispatchToProps)(SubNavComponent);

export default SubNav;
